import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { Userpilot } from 'userpilot';
import { AuthorizeService } from './authorize.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class UserpilotService {
  private readonly authorizeService = inject(AuthorizeService);
  private readonly router = inject(Router);
  private readonly configService = inject(ConfigService);

  private isUserSet = false;
  private getUserSubscription: Subscription | undefined;
  private readonly trackPageReloadsSubscription: Subscription | undefined;

  public run() {
    const userPilotToken = this.getUserPilotToken();
    Userpilot.initialize(userPilotToken);

    this.getUserSubscription = this.identify()
      .pipe(switchMap(() => this.trackPageReloads()))
      .subscribe();
  }

  public stop() {
    this.isUserSet = false;
    this.trackPageReloadsSubscription?.unsubscribe();
    this.getUserSubscription?.unsubscribe();
  }

  private identify() {
    return this.authorizeService.getUser().pipe(
      distinctUntilChanged((prev, current) => prev?.sub === current?.sub),
      tap(user => {
        if (user) {
          // Set user id
          Userpilot.identify(user.sub, {
            company: {
              id: user.userPilotCompanyId,
              name: user.userPilotCompanyName,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              created_at: user.userPilotCompanyCreatedAt,
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            client_id: user.sso_id,
          });
          this.isUserSet = true;
        } else {
          this.isUserSet = false;
        }
      }),
    );
  }

  private trackPageReloads() {
    return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      tap(() => {
        this.reload();
      }),
    );
  }

  /** Tells UserPilot the page has changed. Called on each router navigation, and directly for URL changes made outside the router. */
  public reload() {
    if (this.isUserSet) {
      Userpilot.reload();
    }
  }

  private getUserPilotToken() {
    const userPilotToken = this.configService.userPilotToken;
    if (!userPilotToken) {
      throw new Error('"UserPilot" token is undefined');
    }
    return userPilotToken;
  }

  public trackEvent(eventName: string, payload: Record<string, any>) {
    if (this.isUserSet) {
      Userpilot.track(eventName, payload);
    }
  }
}
